import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Type } from 'lucide-react';
import { api } from '../../api/client.js';
import { useAuthStore } from '../../stores/authStore.js';

interface ComfortStepProps {
  onNext: () => void;
}

// Promotes Comfort Mode into onboarding rather than burying it three sections
// deep in Settings. Choosing "bigger" persists uiMode = 'simplified' (the tier
// labelled "Comfort Mode"), which App.tsx now maps to data-comfort. Each person
// chooses for their own device; the step never blocks - both buttons advance.
export function ComfortStep({ onNext }: ComfortStepProps) {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const [saving, setSaving] = useState(false);

  const choose = async (enable: boolean) => {
    if (saving) return;
    setSaving(true);
    const uiMode = enable ? 'simplified' : 'standard';
    const result = await api.updateProfile({ uiMode });
    if (result.data?.user && user) {
      setUser({ ...user, uiMode: result.data.user.uiMode });
    }
    onNext();
  };

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/15">
          <Type className="h-8 w-8 text-primary" />
        </div>

        <h2
          className="font-display text-2xl font-bold text-ink-1 mb-2 tracking-tight"
          style={{ fontVariationSettings: '"opsz" 32' }}
        >
          {t('wizard.comfort.title')}
        </h2>
        <p className="text-ink-3 max-w-sm mx-auto text-sm leading-relaxed mb-5">
          {t('wizard.comfort.subtitle')}
        </p>

        {/* Sample of the larger comfort text so the choice is concrete */}
        <div className="mx-auto mb-6 max-w-sm rounded-xl bg-surface-2/60 border border-line-1/50 p-4">
          <p className="text-ink-1" style={{ fontSize: '19px', lineHeight: 1.5 }}>
            {t('wizard.comfort.sample')}
          </p>
        </div>

        <div className="flex flex-col gap-3 max-w-sm mx-auto">
          <button
            type="button"
            onClick={() => choose(true)}
            disabled={saving}
            className="btn-primary w-full min-h-[44px] disabled:opacity-50"
          >
            {t('wizard.comfort.enable')}
          </button>
          <button
            type="button"
            onClick={() => choose(false)}
            disabled={saving}
            className="w-full min-h-[44px] rounded-xl bg-surface-3 px-4 py-2.5 text-sm font-medium text-ink-2 hover:bg-surface-sunken transition-colors disabled:opacity-50"
          >
            {t('wizard.comfort.keepStandard')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
